import {Environment, graphql} from 'react-relay';
import Util from '../../forecast-app/shared/util/util';
import * as tracking from '../../tracking';
import {trackAndCommitMutation} from '../../tracking/amplitude/TrackMutation';
import {MutationInput, RelayOnCompleted, RelayOnError} from '../MutationTypes';
import {
	UpdateCustomFieldDefinitionInput,
	UpdateCustomFieldDefinitionMutation,
	UpdateCustomFieldDefinitionMutation$rawResponse,
} from './__generated__/UpdateCustomFieldDefinitionMutation.graphql';
import type {Disposable} from 'relay-runtime';

const mutation = graphql`
	mutation UpdateCustomFieldDefinitionMutation($input: UpdateCustomFieldDefinitionInput!) @raw_response_type {
		updateCustomFieldDefinition(input: $input) {
			customFieldDefinition {
				node {
					id
					displayName
					avaRef
					showOnOpportunityList
					showOnProjectList
				}
			}
		}
	}
`;

function getOptimisticResponse(input: UpdateCustomFieldDefinitionInput): UpdateCustomFieldDefinitionMutation$rawResponse {
	return {
		updateCustomFieldDefinition: {
			customFieldDefinition: {
				node: {
					id: input.id,
					displayName: input.displayName,
					avaRef: input.avaRef,
					// Left out when not sent, so the optimistic update does not overwrite a stored value
					// whose checkbox was hidden.
					...(typeof input.showOnOpportunityList === 'boolean'
						? {showOnOpportunityList: input.showOnOpportunityList}
						: {}),
					...(typeof input.showOnProjectList === 'boolean' ? {showOnProjectList: input.showOnProjectList} : {}),
				},
			},
		},
	} as UpdateCustomFieldDefinitionMutation$rawResponse;
}
function commit(
	environment: Environment,
	input: MutationInput<UpdateCustomFieldDefinitionInput>,
	onSuccess: RelayOnCompleted<UpdateCustomFieldDefinitionMutation>,
	onError: RelayOnError
): Disposable {
	tracking.trackEvent('CustomFieldDefinition - Updated');

	// Capture the real __typename before the mutation so the updater can restore it.
	const originalTypename: string | undefined = input.id
		? (environment.getStore().getSource().get(input.id) as {__typename?: string} | null | undefined)?.__typename
		: undefined;

	return trackAndCommitMutation<UpdateCustomFieldDefinitionMutation>(environment, {
		mutation,
		variables: {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}},
		configs: [],
		optimisticResponse: getOptimisticResponse(input),
		updater: store => {
			if (!input.id) return;
			const record = store.get(input.id);
			if (record) {
				record.setValue(input.readOnly ?? false, 'readOnly');
				if (originalTypename) {
					record.setValue(originalTypename, '__typename');
				}
			}
		},
		onCompleted: onSuccess,
		onError: onError,
	});
}

export default {commit};
